import { useState, useRef } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Type, Image as ImageIcon, Video, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { shieldBlockedMessage } from "@/lib/shield";
import { STORY_BGS, STORY_AUDIENCES, bgCss } from "./storyBg";
import { fileToRawB64, uploadStoryVideo } from "@/lib/storyUpload";

const TABS = [
  { key: "text", label: "Text", icon: Type },
  { key: "image", label: "Photo", icon: ImageIcon },
  { key: "video", label: "Video", icon: Video },
];

// Burn timers (v1.3.4 parity): free 1/6/24h (default 24), 48h is BLOQ+-only.
const STORY_TTLS = [
  { h: 1, label: "1h" },
  { h: 6, label: "6h" },
  { h: 24, label: "24h" },
  { h: 48, label: "48h", pro: true },
];

export function CreateStoryDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const { data: sub } = useQuery({ queryKey: ["subscription"], queryFn: api.subscriptionStatus, staleTime: 60000 });
  const isPaid = !!sub?.subscription && sub.subscription.plan !== "free" && sub.subscription.is_active;
  const [tab, setTab] = useState("text");
  const [expiry, setExpiry] = useState(24);
  const [text, setText] = useState("");
  const [bg, setBg] = useState(STORY_BGS[0].key);
  const [audience, setAudience] = useState("contacts");
  const [imgB64, setImgB64] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);
  const videoRef = useRef(null);

  const reset = () => {
    setTab("text"); setText(""); setBg(STORY_BGS[0].key); setAudience("contacts"); setExpiry(24);
    setImgB64(null); setImgPreview(null); setVideoFile(null); setProgress(0);
  };

  const pickImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image.");
    const b64 = await fileToRawB64(f);
    const cap = isPaid ? 2.4 * 1024 * 1024 : 800 * 1024;
    if (b64.length > cap) {
      return toast.error(isPaid
        ? "Photo is too large (max ~1.8MB)."
        : "Photo is too large — free plan allows ~600KB. Upgrade to BLOQ+ for larger photos.");
    }
    setImgB64(b64);
    setImgPreview(URL.createObjectURL(f));
  };

  const pickVideo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("video/")) return toast.error("Please choose a video.");
    if (f.size > 60 * 1024 * 1024) return toast.error("Video is too large (max 60MB).");
    // Reject clips over 60s early to avoid a wasted upload (server enforces the same cap).
    const url = URL.createObjectURL(f);
    const probe = document.createElement("video");
    probe.preload = "metadata";
    probe.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      if (probe.duration && probe.duration > 61) return toast.error("Video is too long (max 60s).");
      setVideoFile(f);
    };
    probe.onerror = () => { URL.revokeObjectURL(url); setVideoFile(f); };
    probe.src = url;
  };

  const createMut = useMutation({
    mutationFn: async () => {
      const base = { audience, expiry_h: expiry };
      if (tab === "text") {
        if (!text.trim()) throw new Error("Write something first.");
        return api.createStory({ ...base, kind: "text", text: text.trim(), bg });
      }
      if (tab === "image") {
        if (!imgB64) throw new Error("Choose a photo first.");
        return api.createStory({ ...base, kind: "image", image_b64: imgB64, ...(text.trim() ? { text: text.trim() } : {}) });
      }
      // video
      if (!videoFile) throw new Error("Choose a video first.");
      const mediaId = await uploadStoryVideo(videoFile, setProgress);
      if (!mediaId) throw new Error("Upload failed.");
      return api.createStory({ ...base, kind: "video", media_id: mediaId, ...(text.trim() ? { text: text.trim() } : {}) });
    },
    onSuccess: () => {
      const ttlLabel = STORY_TTLS.find((t) => t.h === expiry)?.label || `${expiry}h`;
      toast.success(`Story posted — live for ${ttlLabel}`);
      qc.invalidateQueries({ queryKey: ["stories-feed"] });
      reset();
      onOpenChange(false);
    },
    onError: (e) => toast.error(shieldBlockedMessage(e) || e?.response?.data?.detail?.message || e?.response?.data?.detail || e?.message || "Couldn't post your story."),
  });

  const busy = createMut.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { onOpenChange(v); if (!v) reset(); } }}>
      <DialogContent className="border-white/10 bg-bloq-s1 text-white sm:max-w-md" data-testid="create-story-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Add to your story</DialogTitle>
        </DialogHeader>

        <div className="flex gap-1.5 rounded-2xl bg-bloq-s2 p-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={cn(
                "flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2 text-xs font-semibold transition-colors",
                tab === t.key ? "bg-bloq-mint/15 text-bloq-mint" : "text-bloq-muted hover:text-white",
              )}
              data-testid={`story-tab-${t.key}`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "text" && (
          <div className="mt-1 space-y-3">
            <div
              className="flex min-h-[200px] items-center justify-center rounded-2xl p-5"
              style={{ background: bgCss(bg) }}
            >
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={300}
                placeholder="Type a story…"
                className="w-full resize-none bg-transparent text-center text-xl font-semibold text-white placeholder:text-white/60 outline-none"
                rows={4}
                data-testid="story-text-input"
              />
            </div>
            <div className="flex gap-2">
              {STORY_BGS.map((b) => (
                <button
                  key={b.key}
                  onClick={() => setBg(b.key)}
                  className={cn("h-7 w-7 rounded-full ring-2 transition-all", bg === b.key ? "ring-white scale-110" : "ring-transparent")}
                  style={{ background: bgCss(b.key) }}
                  data-testid={`story-bg-${b.key}`}
                  aria-label={b.key}
                />
              ))}
            </div>
          </div>
        )}

        {tab === "image" && (
          <div className="mt-1 space-y-3">
            <button
              onClick={() => fileRef.current?.click()}
              className="flex min-h-[200px] w-full items-center justify-center overflow-hidden rounded-2xl border border-dashed border-white/20 bg-bloq-s2"
              data-testid="story-image-pick"
            >
              {imgPreview ? (
                <img src={imgPreview} alt="" className="max-h-[240px] w-full object-contain" />
              ) : (
                <span className="flex flex-col items-center gap-2 text-bloq-muted"><ImageIcon className="h-8 w-8" /> Tap to choose a photo</span>
              )}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickImage} data-testid="story-image-file" />
            <input value={text} onChange={(e) => setText(e.target.value)} maxLength={200} placeholder="Add a caption (optional)" className="w-full rounded-xl border border-white/12 bg-bloq-s2 px-3 py-2 text-sm outline-none focus:border-bloq-mint/40" data-testid="story-image-caption" />
          </div>
        )}

        {tab === "video" && (
          <div className="mt-1 space-y-3">
            <button
              onClick={() => videoRef.current?.click()}
              className="flex min-h-[200px] w-full items-center justify-center rounded-2xl border border-dashed border-white/20 bg-bloq-s2"
              data-testid="story-video-pick"
            >
              {videoFile ? (
                <span className="flex flex-col items-center gap-2 text-bloq-mint"><Video className="h-8 w-8" /> {videoFile.name}</span>
              ) : (
                <span className="flex flex-col items-center gap-2 text-bloq-muted"><Video className="h-8 w-8" /> Tap to choose a video (max 60MB · 60s)</span>
              )}
            </button>
            <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={pickVideo} data-testid="story-video-file" />
            {busy && progress > 0 && (
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10"><div className="h-full bg-bloq-mint transition-all" style={{ width: `${progress}%` }} /></div>
            )}
          </div>
        )}

        <div className="mt-1">
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-bloq-muted">Disappears after</div>
          <div className="flex gap-1.5">
            {STORY_TTLS.map((t) => {
              const locked = t.pro && !isPaid;
              const active = expiry === t.h;
              return (
                <button
                  key={t.h}
                  onClick={() => {
                    if (locked) { toast.message("48-hour stories are a BLOQ+ perk"); return; }
                    setExpiry(t.h);
                  }}
                  className={cn(
                    "flex-1 rounded-xl border px-3 py-2 text-xs font-semibold transition-colors",
                    active ? "border-bloq-mint/40 bg-bloq-mint/10 text-bloq-mint" : "border-white/12 text-bloq-muted hover:text-white",
                    locked && "opacity-60",
                  )}
                  data-testid={`story-ttl-${t.h}`}
                >
                  {t.label}{t.pro && <span className="ml-0.5 align-middle text-[10px] font-bold text-bloq-purple">+</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-1">
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-bloq-muted">Who can see this</div>
          <div className="flex gap-1.5">
            {STORY_AUDIENCES.filter((a) => a.key !== "bloq").map((a) => (
              <button
                key={a.key}
                onClick={() => setAudience(a.key)}
                className={cn("flex-1 rounded-xl border px-3 py-2 text-xs font-semibold transition-colors", audience === a.key ? "border-bloq-mint/40 bg-bloq-mint/10 text-bloq-mint" : "border-white/12 text-bloq-muted hover:text-white")}
                data-testid={`story-audience-${a.key}`}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={() => createMut.mutate()}
          disabled={busy}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-2xl bloq-gradient py-3 text-sm font-bold text-bloq-bg transition-opacity hover:opacity-90 disabled:opacity-50"
          data-testid="story-post-btn"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {busy ? "Posting…" : "Share to story"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
